import React, { useMemo } from "react";
import { PlayerStatisticsListV2, StatisticKeyEnum } from "../../../../client/api";
import PlayerStatisticPanel, { PlayerStatData } from "./PlayerStatisticPanel";
import PremiumBlurPanel from "./PremiumBlurPanel";

function extractStatisticData(
  playerStats: PlayerStatisticsListV2 | null,
  statisticKey: StatisticKeyEnum,
  limit = 3
): PlayerStatData[] {
  if (!playerStats) return [];
  
  const stat = playerStats.find((s) => s.statistic.key === statisticKey);
  if (!stat) return [];
  
  return stat.playerStatistics.slice(0, limit).map(({ player, value, placement }) => ({
    player,
    value,
    placement,
  }));
}

type StatisticsPanelsProps = {
  playerStats: PlayerStatisticsListV2 | null;
  isPremiumUser: boolean;
  hasRestriction: boolean;
};

const StatisticsPanels: React.FC<StatisticsPanelsProps> = ({ 
  playerStats, 
  isPremiumUser, 
  hasRestriction 
}) => {
  const shouldBlurPremiumContent = hasRestriction && !isPremiumUser;
  
  const evaluationData = useMemo(
    () => extractStatisticData(playerStats, StatisticKeyEnum.PLAYER_RATING),
    [playerStats]
  );

  const topScorerData = useMemo(
    () => extractStatisticData(playerStats, StatisticKeyEnum.PLAYER_MOST_GOALS),
    [playerStats]
  );

  const mostAssistData = useMemo(
    () => extractStatisticData(playerStats, StatisticKeyEnum.PLAYER_MOST_ASSISTS),
    [playerStats]
  );

  const mostMinutesData = useMemo(
    () => extractStatisticData(playerStats, StatisticKeyEnum.PLAYER_MOST_MINUTES),
    [playerStats]
  );

  if (!isPremiumUser) {
    return (
      <>
        {topScorerData.length > 0 && (
          <PlayerStatisticPanel
            titleKey="topScorerTitle"
            players={topScorerData}
          />
        )}
        {mostMinutesData.length > 0 && (
          <PlayerStatisticPanel
            titleKey="mostMinutes"
            players={mostMinutesData}
          />
        )}
        {shouldBlurPremiumContent && <PremiumBlurPanel />}
      </>
    );
  }

  return (
    <>
      {evaluationData.length > 0 && (
        <PlayerStatisticPanel
          titleKey="evaluation"
          players={evaluationData}
          formatAsDecimal
        />
      )}
      {topScorerData.length > 0 && (
        <PlayerStatisticPanel
          titleKey="topScorerTitle"
          players={topScorerData}
        />
      )}
      {mostAssistData.length > 0 && (
        <PlayerStatisticPanel
          titleKey="assists"
          players={mostAssistData}
        />
      )}
      {mostMinutesData.length > 0 && (
        <PlayerStatisticPanel
          titleKey="mostMinutes"
          players={mostMinutesData}
        />
      )}
    </>
  );
};

export default StatisticsPanels;

